import React, { forwardRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

/* ─────────── Helpers ─────────── */
const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const currencySymbol = (code) => (code === 1 ? "؋" : code === 2 ? "$" : "€");

/* ─────────── Static styles ─────────── */
const styles = {
  wrapper: (direction) => ({
    fontFamily: "Arial, sans-serif",
    padding: 15,
    boxSizing: "border-box",
    direction,
  }),
  hr: {
    width: "100%",
    height: 1,
    background: "#9fb0cc",
    margin: "30px 0",
  },
  labelValueRow: {
    marginBottom: 10,
    display: "flex",
    justifyContent: "space-between",
    borderBottom: "1px solid #b1c7b7",
  },
};

const PrintPayment = forwardRef(({ data }, ref) => {
  const { t } = useTranslation();
  const direction = localStorage.getItem("direction");

  /* ─────────── Print-only CSS injection ─────────── */
  useEffect(() => {
    const printCSS = `
      @media print {
        @page { size:A4; margin:0; }
        html,body{
          width:210mm;height:297mm;margin:0!important;padding:0!important;
          background:#fff!important;
        }
        body *{ visibility:hidden; }
        #printable, #printable *{ visibility:visible; }
        #printable{ position:absolute;inset:0;width:100%;margin:0;padding:0; }
      }
    `;
    const style = Object.assign(document.createElement("style"), {
      textContent: printCSS,
    });
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  /* ─────────── Rows for payment details ─────────── */
  const paymentRows = useMemo(
    () => [
      {
        label: t("reception.totalPrice"),
        value: `${data?.total_amount} Afn`,
      },

      {
        label: t("reception.amountPaid"),
        value: `${data.total_paid} Afn`,
      },

      {
        label: t("reception.balanceDue"),
        value: `${data.balance_due} Afn`,
      },
    ],
    [data, t]
  );

  return (
    <div ref={ref} id="printable" style={styles.wrapper(direction)}>
      {/* ───────── Header ───────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 15,
        }}
      >
        <img
          src={`/daudzai-logo.png`}
          alt="Logo"
          style={{ width: 150, height: 150 }}
        />
      </div>

      {/* Invoice number & date */}
      <div style={{ marginBottom: 10 }}>
        <p style={{ margin: 0 }}>
          {t("reception.invoice")}:{" "}
          <strong style={{ marginLeft: 8 }}>{data?.id}</strong>
        </p>
        <p style={{ margin: 0 }}>
          {t("reception.paymentDate")}:{" "}
          <strong style={{ marginLeft: 8 }}>{formatDate(data?.date)}</strong>
        </p>
      </div>

      {/* Title with lines */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 15,
          height: 50,
        }}
      >
        <div style={{ flex: 1, height: 1, background: "#000" }} />
        <p style={{ margin: "0 10px", textTransform: "capitalize" }}>
          INVOICE BILL
        </p>
        <div style={{ flex: 1, height: 1, background: "#000" }} />
      </div>

      {/* Patient Info */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 15,
        }}
      >
        <span>
          {t("reception.patient")}: <strong>{data.patient_info?.name}</strong>
        </span>
        <span>
          {t("reception.contact")}: <strong>{data.patient_info?.phone}</strong>
        </span>
        <span>
          {t("reception.age")}: <strong>{data.patient_info?.age}</strong>
        </span>
        <span>
          {t("reception.date")}: <strong>{formatDate(data.date)}</strong>
        </span>
      </div>

      {/* Divider */}
      <div style={styles.hr} />

      {/* Blue box */}

      <div
        style={{ display: "flex", justifyContent: "center", height: 145 }}
      ></div>

      {/* Divider */}
      <div style={styles.hr} />

      {/* Payment details */}
      <div style={{ fontSize: 14, lineHeight: 1.6, color: "#333" }}>
        {paymentRows.map(({ label, value }) => (
          <div key={label} style={styles.labelValueRow}>
            <strong>{label}:</strong>
            <span>{value}</span>
          </div>
        ))}
      </div>

      {/* Non-refundable note */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "2px solid gray",
          height: 30,
          marginTop: 15,
        }}
      >
        <p style={{ margin: 0, fontWeight: "bold" }}>
          پول تادیه شده مسترد نمی‌گردد
        </p>
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", color: "#555", marginTop: 20 }}>
        <p style={{ margin: 8 }}>{t("print.thanks")}</p>
        <p style={{ margin: 8 }}>{t("print.getWellSoon")}</p>
        <p style={{ margin: 8, fontSize: 12 }}>{t("print.cosmos")}</p>

        <h6 style={{ margin: 0, fontWeight: "bold" }}>
          {t("print.turkishClinic")}
        </h6>
        <p style={{ margin: 4, fontSize: 12 }}>
          {t("print.phone")}: {t("print.phoneNumber")}
        </p>
        <p style={{ margin: 4, fontSize: 12 }}>{t("print.address")}</p>
      </div>
    </div>
  );
});

export default PrintPayment;

